import { parseCookieHeader } from "@next-platform/packages-ts-types";
import { captureAttributionFromUrl, type ICaptureResult } from "@next-platform/client-common-core";

import { readLegacyPartnerIds } from "@/features/attribution/bridgeLegacyStorage";

/**
 * Browser-only wrapper that reads `window.location.href`,
 * `document.referrer`, `document.cookie` (for Pixel-set first-party
 * cookies like `_fbp`/`_fbc`), and the legacy `aff_id`/`tap_vid` keys
 * from `localStorage`, then forwards everything as explicit inputs to
 * the platform-neutral `captureAttributionFromUrl` helper exported by
 * `spicychat-business-logic`.
 *
 * Lives in the web frontend (not in the cross-platform business-logic
 * shim) because every data source here is browser-specific:
 *
 * - `window.location` / `document.referrer` — no analog on native.
 * - `document.cookie` — React Native has no cookie jar.
 * - Legacy `aff_id` / `tap_vid` keys — only ever written by web code.
 *
 * Returns `undefined` in a non-browser context so the module can still
 * be imported from SSR / RN without crashing (belt-and-braces even
 * though the module is only wired into the web app today).
 */
export async function captureAttributionFromCurrentWindow(): Promise<ICaptureResult | undefined> {
  if (typeof globalThis.window === "undefined" || typeof globalThis.document === "undefined") {
    return undefined;
  }
  return captureAttributionFromUrl({
    url: globalThis.window.location.href,
    referrer: globalThis.document.referrer,
    cookies: parseCookieHeader(globalThis.document.cookie),
    legacyPartners: readLegacyPartnerIds(),
  });
}
